import { useState, useEffect, useCallback, memo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCache, setCache } from '../utils/cache';
import { API_URL } from '../config';
import './PokemonDatabase.css';

// SearchBar component to prevent unnecessary re-renders
const SearchBar = memo(({ onSearch }) => {
  const [inputValue, setInputValue] = useState('');
  const debounceTimeout = useRef(null);

  const handleChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      onSearch(value);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  return (
    <div className="pokedex__search-bar">
      <input
        type="text"
        placeholder="Search by name or type..."
        value={inputValue}
        onChange={handleChange}
      />
    </div>
  );
});

// Pokemon Grid component with its own loading state
const PokemonGrid = memo(({ pokemon, loading, navigate, getStatPercentage, formatStatName }) => {
  if (loading) {
    return <div className="pokemon-grid-loading">Loading Pokémon...</div>;
  }

  return (
    <div className="pokedex__grid">
      {pokemon.map(poke => (
        <article 
          key={poke.pokedexId} 
          className="pokedex__card"
          onClick={() => navigate(poke.pokedexId)}
          style={{ cursor: 'pointer' }}
        >
          <div className="pokedex__card-image">
            <img
              src={poke.sprites?.other?.['official-artwork']?.front_default || 
                  poke.sprites?.front_default || 
                  `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${poke.pokedexId}.png`}
              alt={poke.name}
              loading="lazy"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${poke.pokedexId}.png`;
                e.target.onerror = () => {
                  e.target.src = '/placeholder-pokemon.png';
                  e.target.onerror = null;
                };
              }}
            />
          </div>
          <div className="pokedex__card-info">
            <h2>{poke.name.charAt(0).toUpperCase() + poke.name.slice(1)}</h2>
            <div className="pokedex__types">
              {poke.types.map(type => (
                <span key={`${poke.id}-${type}`} className={`type-badge type-badge--${type.toLowerCase()}`}>
                  {type}
                </span>
              ))}
            </div>
            <div className="pokedex__stats">
              {poke.stats.map((stat) => {
                const percentage = getStatPercentage(stat.name)(stat.base);
                const statColor = percentage > 66 ? '#2ecc71' : percentage > 33 ? '#f1c40f' : '#e74c3c';
                
                return (
                  <div key={`${poke.id}-${stat.name}`} className="pokedex__stat">
                    <label className="pokedex__stat-label">{formatStatName(stat.name)}</label>
                    <div className="pokedex__stat-bar">
                      <div 
                        className="pokedex__stat-bar-fill"
                        data-stat={stat.name}
                        style={{ 
                          width: `${percentage}%`,
                          '--stat-color': statColor
                        }}
                      />
                    </div>
                    <div className="pokedex__stat-value">{stat.base}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </article>
      ))}
    </div>
  );
});

function PokemonDatabase() {
  const navigate = useNavigate();
  const location = useLocation();
  const [pokemon, setPokemon] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    page: 1
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalPokemon, setTotalPokemon] = useState(0);
  const itemsPerPage = 20;
  const abortController = useRef(null);

  // Save scroll position when navigating to a Pokemon detail
  const handlePokemonClick = useCallback((pokemonId) => {
    navigate(`/pokemon/${pokemonId}`);
  }, [navigate]);

  // Reset scroll position when filters change
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      page: 1
    }));
  }, [filters.search]);

  // Helper function to calculate stat percentage
  const getStatPercentage = useCallback((stat) => {
    const maxStats = {
      'hp': 255,
      'attack': 190,
      'defense': 230,
      'special-attack': 194,
      'special-defense': 230,
      'speed': 180
    };
    const maxStat = maxStats[stat.toLowerCase()] || 255;
    return (value) => Math.min((value / maxStat) * 100, 100).toFixed(1);
  }, []);

  // Helper function to format stat name
  const formatStatName = useCallback((stat) => {
    const statNames = {
      'hp': 'HP',
      'attack': 'Attack',
      'defense': 'Defense',
      'special-attack': 'Sp. Atk',
      'special-defense': 'Sp. Def',
      'speed': 'Speed'
    };
    return statNames[stat.toLowerCase()] || stat;
  }, []);

  const fetchPokemon = useCallback(async () => {
    // Cancel any in-flight request
    if (abortController.current) {
      abortController.current.abort();
    }
    
    // Create new abort controller for this request
    abortController.current = new AbortController();
    
    try {
      setLoading(true);
      setError(null);
      
      // Try to get data from cache first
      const cacheKey = `pokemon_page_${filters.page}_search_${filters.search}`;
      const cachedData = getCache(cacheKey);
      
      if (cachedData) {
        setPokemon(cachedData.pokemon);
        setTotalPages(cachedData.pagination.pages);
        setTotalPokemon(cachedData.pagination.total);
        setLoading(false);
        return;
      }

      // If not in cache, fetch from API with search parameter
      const response = await fetch(
        `${API_URL}/pokemon?page=${filters.page}&limit=${itemsPerPage}&search=${filters.search}`,
        { signal: abortController.current.signal }
      );
      
      if (!response.ok) throw new Error('Failed to fetch Pokemon list');
      const result = await response.json();
      
      // Only update state if the request wasn't aborted
      if (!abortController.current.signal.aborted) {
        // Cache the result
        setCache(cacheKey, result);
        
        setPokemon(result.pokemon);
        setTotalPages(result.pagination.pages);
        setTotalPokemon(result.pagination.total);
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        // Ignore abort errors
        return;
      }
      console.error('Error fetching Pokemon:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, itemsPerPage]);

  useEffect(() => {
    fetchPokemon();
    
    return () => {
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, [fetchPokemon]);

  const handleSearch = useCallback((searchTerm) => {
    setFilters(prev => ({
      ...prev,
      search: searchTerm,
      page: 1
    }));
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  }, []);

  if (error) return <div className="error-container">Error: {error}</div>;

  return (
    <div className="pokedex">
      <header className="pokedex__header">
        <h1>Pokédex</h1>
        <SearchBar onSearch={handleSearch} />
        <div className="pokedex__pagination pokedex__pagination--top">
          <button
            onClick={() => handlePageChange(Math.max(1, filters.page - 1))}
            disabled={filters.page === 1}
          >
            Previous
          </button>
          <span>Page {filters.page} of {totalPages}</span>
          <button
            onClick={() => handlePageChange(Math.min(totalPages, filters.page + 1))}
            disabled={filters.page === totalPages}
          >
            Next
          </button>
        </div>
      </header>

      <PokemonGrid 
        pokemon={pokemon}
        loading={loading}
        navigate={handlePokemonClick}
        getStatPercentage={getStatPercentage}
        formatStatName={formatStatName}
      />

      <div className="pokedex__pagination">
        <button
          onClick={() => handlePageChange(Math.max(1, filters.page - 1))}
          disabled={filters.page === 1}
        >
          Previous
        </button>
        <span>Page {filters.page} of {totalPages}</span>
        <button
          onClick={() => handlePageChange(Math.min(totalPages, filters.page + 1))}
          disabled={filters.page === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default PokemonDatabase;